import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../assets/css/header.css";
import navData from "../assets/data/nav.json";

const Header = () => {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleScroll = useCallback(() => {
        const offset = window.scrollY;
        const hasScrolled = offset > 50;
        setScrolled(hasScrolled);
        setIsVisible(hasScrolled);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen((currentValue) => !currentValue);
    };

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    const scrollToSection = useCallback(
        (id) => {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }

            // Supprimer le paramètre `scrollTo` de l'URL sans recharger la page
            const url = new URL(window.location);
            url.searchParams.delete("scrollTo");
            window.history.replaceState({}, "", url);

            closeMenu();
        },
        [closeMenu],
    );

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        closeMenu();
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        handleScroll();

        const handleResize = () => {
            if (window.innerWidth > 991) {
                closeMenu();
            }
        };

        window.addEventListener("resize", handleResize);

        const params = new URLSearchParams(window.location.search);
        const sectionId = params.get("scrollTo");
        let timeoutId;

        if (sectionId) {
            timeoutId = setTimeout(() => {
                scrollToSection(sectionId);
            }, 500);
        }

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [closeMenu, handleScroll, scrollToSection]);

    useEffect(() => {
        if (window.innerWidth < 992 && isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    const logoSrc = scrolled ? navData.logo.scrolled : navData.logo.default;

    return (
        <>
            <div
                className={`header ${scrolled ? "header-scrolled header-pinned" : ""} ${isMenuOpen ? "header-open" : ""}`}
                id="header"
            >
                <div className="header-inner">
                    <button
                        className="header-brand"
                        type="button"
                        onClick={() => scrollToSection("social")}
                        aria-label={t("header.aria.home")}
                    >
                        <img
                            src={logoSrc}
                            alt="Logo"
                            className="header-logo"
                            draggable="false"
                        />
                    </button>

                    <button
                        className="header-burger"
                        type="button"
                        onClick={toggleMenu}
                        aria-controls="main-navigation"
                        aria-expanded={isMenuOpen}
                        aria-label={
                            isMenuOpen ?
                                t("header.aria.closeMenu")
                            :   t("header.aria.openMenu")
                        }
                    >
                        <span className="header-burger-line" />
                        <span className="header-burger-line" />
                        <span className="header-burger-line" />
                    </button>

                    <nav
                        id="main-navigation"
                        className={`header-nav ${isMenuOpen ? "header-nav-open" : ""}`}
                        aria-label={t("header.aria.mainNavigation")}
                    >
                        <ul className="header-list">
                            {navData.menuItems.map((item) => (
                                <li key={item.id} className="header-item">
                                    <button
                                        className="header-link"
                                        onClick={() => scrollToSection(item.id)}
                                    >
                                        {t(item.labelKey)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <button
                        className={`header-backdrop ${isMenuOpen ? "header-backdrop-visible" : ""}`}
                        type="button"
                        onClick={closeMenu}
                        aria-label={t("header.aria.closeNavigation")}
                        tabIndex={-1}
                    />
                </div>
            </div>

            {/* Chevron */}

            {isVisible && (
                <button
                    className="scroll-top"
                    onClick={scrollToTop}
                    aria-label={t("header.scrollTop")}
                >
                    <i className="fa-solid fa-chevron-up"></i>
                </button>
            )}
        </>
    );
};

export default Header;
